import React, { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Save,
  Upload,
  AlertCircle,
  Clock,
  FileText,
  Tag,
  Info,
  HelpCircle,
} from "lucide-react";
import {
  AdminLayout,
  Card,
  Button,
  Badge,
  Modal,
  Breadcrumb,
} from "../components/AdminSharedComponents";
import api from "../../../../utils/api";
import ReactMarkdown from "react-markdown";

// Markdown preview component
const MarkdownPreview = ({ content }) => {
  return <ReactMarkdown>{content || "Preview will appear here"}</ReactMarkdown>;
};

const AddResearch = () => {
  const navigate = useNavigate();
  const contentRef = useRef(null);
  const [activeTab, setActiveTab] = useState("write");
  const [showHelpModal, setShowHelpModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [unsavedChanges, setUnsavedChanges] = useState(false);
  const [showUnsavedModal, setShowUnsavedModal] = useState(false);
  const [exitPath, setExitPath] = useState("");

  const [formData, setFormData] = useState({
    title: "",
    area: "",
    description: "",
    content: "",
    author: "",
    keywords: [],
    keywordInput: "",
    references: "",
    thumbnail: null,
    thumbnailPreview: null,
    images: [],
    imagesPreview: [],
    status: "draft",
  });

  const prepareFormData = () => {
    const apiFormData = new FormData();

    apiFormData.append("title", formData.title);
    apiFormData.append("description", formData.description);
    apiFormData.append("content", formData.content);
    apiFormData.append("author", formData.author);
    apiFormData.append("area", formData.area);
    apiFormData.append("references", formData.references);
    apiFormData.append("status", formData.status);

    // Kirim keywords dalam format JSON
    apiFormData.append("keywords", JSON.stringify(formData.keywords));

    if (formData.thumbnail) {
      apiFormData.append("thumbnail", formData.thumbnail);
    }

    // Batasi jumlah gambar maksimal 10
    if (formData.images.length > 10) {
      alert("Maksimal 10 gambar yang dapat diunggah.");
    } else {
      formData.images.forEach((image) => {
        apiFormData.append("images", image);
      });
    }

    return apiFormData;
  };

  const [errors, setErrors] = useState({});

  const templateOptions = [
    {
      name: "Research Paper",
      template: `# Research Title\n\n## Abstract\nBrief summary of the research.\n\n## Introduction\nBackground and purpose of the research.`,
    },
    {
      name: "Technical Report",
      template: `# Technical Report\n\n## Executive Summary\nBrief overview of findings and recommendations.\n\n## Problem Statement\nDescription of the issue being addressed.`,
    },
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });

    setUnsavedChanges(true);

    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: null,
      });
    }
  };

  const handleContentChange = (content) => {
    setFormData({ ...formData, content });
    setUnsavedChanges(true);
    if (errors.content) setErrors({ ...errors, content: null });
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData({
        ...formData,
        thumbnail: file,
        thumbnailPreview: URL.createObjectURL(file),
      });
      setUnsavedChanges(true);
    }
  };

  const handleKeywordAdd = () => {
    if (
      formData.keywordInput.trim() &&
      !formData.keywords.includes(formData.keywordInput.trim())
    ) {
      setFormData({
        ...formData,
        keywords: [...formData.keywords, formData.keywordInput.trim()],
        keywordInput: "",
      });
      setUnsavedChanges(true);
    }
  };

  const handleKeywordInputKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleKeywordAdd();
    }
  };

  const handleKeywordRemove = (keywordToRemove) => {
    setFormData({
      ...formData,
      keywords: formData.keywords.filter(
        (keyword) => keyword !== keywordToRemove
      ),
    });
    setUnsavedChanges(true);
  };

  const applyTemplate = (template) => {
    setFormData({
      ...formData,
      content: template,
    });
    setUnsavedChanges(true);
  };

  const handleImagesUpload = (e) => {
    const files = Array.from(e.target.files);
    if (files.length + formData.images.length > 10) {
      alert("Maksimal 10 gambar yang dapat diunggah.");
    } else {
      const newPreviews = files.map((file) => URL.createObjectURL(file));
      setFormData({
        ...formData,
        images: [...formData.images, ...files],
        imagesPreview: [...formData.imagesPreview, ...newPreviews],
      });
      setUnsavedChanges(true);
    }
  };

  const handleImageRemove = (index) => {
    URL.revokeObjectURL(formData.imagesPreview[index]);

    const newImages = [...formData.images];
    const newPreviews = [...formData.imagesPreview];
    newImages.splice(index, 1);
    newPreviews.splice(index, 1);

    setFormData({
      ...formData,
      images: newImages,
      imagesPreview: newPreviews,
    });
    setUnsavedChanges(true);
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.title.trim()) newErrors.title = "Title is required";
    if (!formData.area) newErrors.area = "Research area is required";
    if (!formData.description.trim())
      newErrors.description = "Description is required";
    if (!formData.content.trim()) newErrors.content = "Content is required";
    if (!formData.author.trim()) newErrors.author = "Author is required";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    setLoading(true);

    try {
      const apiFormData = prepareFormData();
      const response = await api.post("/api/research", apiFormData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (response.data) {
        setUnsavedChanges(false);
        setTimeout(() => navigate("/admin/researchs"), 1500);
      }
    } catch (error) {
      setErrors({
        ...errors,
        form: error.response?.data?.message || "Failed to save research",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleNavigation = (path) => {
    if (unsavedChanges) {
      setExitPath(path);
      setShowUnsavedModal(true);
    } else {
      navigate(path);
    }
  };

  const confirmNavigation = () => {
    setShowUnsavedModal(false);
    navigate(exitPath);
  };

  return (
    <AdminLayout>
      <div className="bg-gray-50 min-h-screen">
        {/* Breadcrumb Navigation */}
        <Breadcrumb
          items={[
            { label: "Research", path: "/admin/researchs" },
            { label: "Add New Research", path: "/admin/researchs/add" },
          ]}
          className="p-4 bg-white shadow-sm"
        />

        {/* Header with Actions */}
        <div className="flex items-center justify-between mb-6 bg-white p-4 shadow-sm">
          <div className="flex items-center">
            <Button
              variant="text"
              className="mr-4 p-2 hover:bg-gray-100 rounded-full transition-colors"
              onClick={() => handleNavigation("/admin/researchs")}
              aria-label="Go back"
            >
              <ArrowLeft size={20} className="text-gray-600" />
            </Button>
            <h1 className="text-2xl font-bold text-gray-800">
              Add New Research
            </h1>
          </div>
          <div className="flex items-center space-x-2">
            <Badge variant="default" className="flex items-center">
              <Clock size={14} className="mr-1" />
              <span>Auto-save enabled</span>
            </Badge>
          </div>
        </div>

        <div className="px-6 pb-12">
          {errors.form && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 text-red-700 rounded flex items-center">
              <AlertCircle size={20} className="mr-2" />
              {errors.form}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="md:col-span-2 space-y-6">
                <Card
                  title="Research Information"
                  accentColor="primary"
                  className="shadow-md"
                >
                  <div className="space-y-4 p-4">
                    <div>
                      <label htmlFor="title" className="block mb-2 font-medium">
                        Research Title <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        id="title"
                        name="title"
                        value={formData.title}
                        onChange={handleChange}
                        placeholder="Enter research title"
                        className={`w-full p-3 border rounded ${
                          errors.title ? "border-red-500" : "border-gray-300"
                        }`}
                      />
                      {errors.title && (
                        <p className="mt-1 text-red-500 text-sm">
                          {errors.title}
                        </p>
                      )}
                    </div>

                    <div>
                      <label
                        htmlFor="author"
                        className="block mb-2 font-medium"
                      >
                        Author <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        id="author"
                        name="author"
                        value={formData.author}
                        onChange={handleChange}
                        placeholder="Enter author name"
                        className={`w-full p-3 border rounded ${
                          errors.author ? "border-red-500" : "border-gray-300"
                        }`}
                      />
                      {errors.author && (
                        <p className="mt-1 text-red-500 text-sm">
                          {errors.author}
                        </p>
                      )}
                    </div>

                    <div>
                      <label
                        htmlFor="description"
                        className="block mb-2 font-medium"
                      >
                        Description <span className="text-red-500">*</span>
                      </label>
                      <textarea
                        id="description"
                        name="description"
                        value={formData.description}
                        onChange={handleChange}
                        rows={3}
                        placeholder="Brief description of the research"
                        className={`w-full p-3 border rounded resize-y ${
                          errors.description
                            ? "border-red-500"
                            : "border-gray-300"
                        }`}
                      />
                      {errors.description && (
                        <p className="mt-1 text-red-500 text-sm">
                          {errors.description}
                        </p>
                      )}
                    </div>

                    <div>
                      <div className="flex justify-between items-center mb-2">
                        <label htmlFor="keywords" className="font-medium">
                          Keywords
                        </label>
                        {/* <Tooltip content="Add keywords to improve searchability">
                          <div className="cursor-help">
                            <HelpCircle size={16} className="text-gray-400" />
                          </div>
                        </Tooltip> */}
                        <div content="Add keywords to improve searchability">
                          <div className="cursor-help">
                            <HelpCircle size={16} className="text-gray-400" />
                          </div>
                        </div>
                      </div>
                      <div className="flex mb-2">
                        <input
                          type="text"
                          id="keywordInput"
                          name="keywordInput"
                          value={formData.keywordInput}
                          onChange={handleChange}
                          onKeyDown={handleKeywordInputKeyDown}
                          placeholder="Add keyword and press Enter"
                          className="flex-grow p-3 border border-gray-300 rounded-l"
                        />
                        <button
                          type="button"
                          onClick={handleKeywordAdd}
                          className="px-4 bg-gray-100 border border-gray-300 border-l-0 rounded-r hover:bg-gray-200"
                        >
                          Add
                        </button>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {formData.keywords.map((keyword, index) => (
                          <Badge
                            key={index}
                            variant="default"
                            className="flex items-center space-x-1 px-3 py-2"
                          >
                            <span>{keyword}</span>
                            <button
                              type="button"
                              onClick={() => handleKeywordRemove(keyword)}
                              className="ml-1 text-gray-500 hover:text-gray-700"
                            >
                              ×
                            </button>
                          </Badge>
                        ))}
                        {formData.keywords.length === 0 && (
                          <span className="text-gray-400 text-sm italic">
                            No keywords added
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </Card>

                <Card
                  title="Research Content"
                  accentColor="primary"
                  className="shadow-md"
                >
                  <div className="p-4">
                    <div className="mb-4">
                      <div className="flex justify-between mb-2">
                        <div className="flex space-x-2">
                          <button
                            type="button"
                            className={`px-4 py-1 rounded-t ${
                              activeTab === "write"
                                ? "bg-blue-50 text-blue-600 border-t border-x border-blue-200"
                                : "text-gray-600"
                            }`}
                            onClick={() => setActiveTab("write")}
                          >
                            Write
                          </button>
                          <button
                            type="button"
                            className={`px-4 py-1 rounded-t ${
                              activeTab === "preview"
                                ? "bg-blue-50 text-blue-600 border-t border-x border-blue-200"
                                : "text-gray-600"
                            }`}
                            onClick={() => setActiveTab("preview")}
                          >
                            Preview
                          </button>
                        </div>
                        <div className="flex items-center">
                          <div className="relative mr-2">
                            <Button
                              variant="text"
                              onClick={() => setShowHelpModal(true)}
                              className="text-gray-500 hover:bg-gray-100 p-2 rounded"
                            >
                              <HelpCircle size={16} />
                            </Button>
                          </div>
                          <select
                            className="p-2 border border-gray-300 rounded text-sm"
                            value=""
                            onChange={(e) => {
                              const template = templateOptions.find(
                                (t) => t.name === e.target.value
                              );
                              if (template) {
                                const confirm = applyTemplate(
                                  template.template
                                );
                                if (typeof confirm === "function") {
                                  confirm();
                                }
                              }
                              e.target.value = "";
                            }}
                          >
                            <option value="" disabled>
                              Apply template
                            </option>
                            {templateOptions.map((template, index) => (
                              <option key={index} value={template.name}>
                                {template.name}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>

                      <div
                        className={`border rounded-md ${
                          errors.content ? "border-red-500" : "border-gray-300"
                        }`}
                      >
                        {activeTab === "write" ? (
                          <textarea
                            id="content"
                            name="content"
                            ref={contentRef}
                            value={formData.content}
                            onChange={(e) =>
                              handleContentChange(e.target.value)
                            }
                            rows={15}
                            placeholder="Enter research content (Markdown supported)"
                            className="w-full p-3 font-mono resize-y border-0 focus:ring-0"
                          />
                        ) : (
                          <div className="p-3 prose prose-blue max-w-none min-h-[20rem] bg-gray-50 rounded-md">
                            <MarkdownPreview content={formData.content} />
                          </div>
                        )}
                      </div>
                      {errors.content && (
                        <p className="mt-1 text-red-500 text-sm">
                          {errors.content}
                        </p>
                      )}
                    </div>

                    <div>
                      <label
                        htmlFor="references"
                        className="block mb-2 font-medium"
                      >
                        References
                      </label>
                      <textarea
                        id="references"
                        name="references"
                        value={formData.references}
                        onChange={handleChange}
                        rows={4}
                        placeholder="List references (one per line)"
                        className="w-full p-3 border border-gray-300 rounded resize-y"
                      />
                    </div>
                  </div>
                </Card>
              </div>

              <div className="space-y-6">
                <Card
                  title="Research Settings"
                  accentColor="primary"
                  className="shadow-md"
                >
                  <div className="p-4 space-y-4">
                    <div>
                      <label htmlFor="area" className="block mb-2 font-medium">
                        Research Area <span className="text-red-500">*</span>
                      </label>
                      <select
                        id="area"
                        name="area"
                        value={formData.area}
                        onChange={handleChange}
                        className={`w-full p-3 border rounded ${
                          errors.area ? "border-red-500" : "border-gray-300"
                        }`}
                      >
                        <option value="">Select an area</option>
                        <option value="minerals">Mineral Research</option>
                        <option value="cathodic">Cathodic Protection</option>
                        <option value="corrosion">Corrosion Science</option>
                      </select>
                      {errors.area && (
                        <p className="mt-1 text-red-500 text-sm">
                          {errors.area}
                        </p>
                      )}
                    </div>

                    <div>
                      <label
                        htmlFor="status"
                        className="block mb-2 font-medium"
                      >
                        Status
                      </label>
                      <div className="grid grid-cols-3 gap-2">
                        {["draft", "active", "archived"].map((status) => (
                          <div
                            key={status}
                            className={`
                              border rounded p-3 text-center cursor-pointer
                              ${
                                formData.status === status
                                  ? "bg-blue-50 border-blue-300"
                                  : "border-gray-300 hover:bg-gray-50"
                              }
                            `}
                            onClick={() => setFormData({ ...formData, status })}
                          >
                            <Badge
                              variant={
                                status === "active"
                                  ? "success"
                                  : status === "archived"
                                  ? "danger"
                                  : "default"
                              }
                              className="rounded-full px-2 py-1 text-xs"
                            >
                              {status.charAt(0).toUpperCase() + status.slice(1)}
                            </Badge>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div>
                      <label
                        htmlFor="thumbnail"
                        className="block mb-2 font-medium"
                      >
                        Featured Image
                      </label>
                      <div
                        className={`border-2 border-dashed rounded-lg p-4 text-center ${
                          formData.thumbnailPreview
                            ? ""
                            : "border-gray-300 hover:border-gray-400"
                        }`}
                      >
                        {formData.thumbnailPreview ? (
                          <div className="relative">
                            <img
                              src={formData.thumbnailPreview}
                              alt="Thumbnail preview"
                              className="max-h-48 mx-auto rounded"
                            />
                            <button
                              type="button"
                              className="absolute top-2 right-2 bg-white rounded-full p-1 shadow"
                              onClick={() =>
                                setFormData({
                                  ...formData,
                                  thumbnail: null,
                                  thumbnailPreview: null,
                                })
                              }
                            >
                              ✕
                            </button>
                          </div>
                        ) : (
                          <label
                            htmlFor="thumbnail-input"
                            className="cursor-pointer flex flex-col items-center py-4"
                          >
                            <Upload size={32} className="text-gray-400 mb-2" />
                            <span className="text-sm text-gray-500">
                              Click to upload image
                            </span>
                            <span className="text-xs text-gray-400 mt-1">
                              JPG, PNG, GIF up to 5MB
                            </span>
                          </label>
                        )}
                        <input
                          type="file"
                          id="thumbnail-input"
                          accept="image/*"
                          className="hidden"
                          onChange={handleImageUpload}
                        />
                      </div>
                    </div>

                    {/* Add this to the Research Information or Research Settings card */}
                    <div>
                      <label
                        htmlFor="images"
                        className="block mb-2 font-medium"
                      >
                        Additional Images
                      </label>
                      <div className="border-2 border-dashed rounded-lg p-4">
                        <div className="grid grid-cols-3 gap-2 mb-3">
                          {formData.imagesPreview.map((preview, index) => (
                            <div key={index} className="relative">
                              <img
                                src={preview}
                                alt={`Additional image ${index + 1}`}
                                className="h-24 w-full object-cover rounded"
                              />
                              <button
                                type="button"
                                className="absolute top-1 right-1 bg-white rounded-full p-1 shadow"
                                onClick={() => handleImageRemove(index)}
                              >
                                ✕
                              </button>
                            </div>
                          ))}
                        </div>

                        <label
                          htmlFor="images-input"
                          className="cursor-pointer flex flex-col items-center py-4"
                        >
                          <Upload size={32} className="text-gray-400 mb-2" />
                          <span className="text-sm text-gray-500">
                            Add additional images
                          </span>
                          <span className="text-xs text-gray-400 mt-1">
                            Up to 10 images
                          </span>
                        </label>
                        <input
                          type="file"
                          id="images-input"
                          accept="image/*"
                          multiple
                          className="hidden"
                          onChange={handleImagesUpload}
                        />
                      </div>
                    </div>
                  </div>
                </Card>

                <Card
                  title="Publication Details"
                  accentColor="primary"
                  className="shadow-md"
                >
                  <div className="p-4 space-y-4">
                    <div className="flex items-center p-3 bg-yellow-50 border border-yellow-200 rounded text-yellow-800">
                      <Info size={18} className="mr-2 flex-shrink-0" />
                      <p className="text-sm">
                        After saving, you can add collaborators and schedule
                        publication.
                      </p>
                    </div>

                    <div className="border rounded p-3 flex items-center justify-between bg-gray-50 text-gray-500">
                      <div className="flex items-center">
                        <FileText size={18} className="mr-2" />
                        <span>Research ID</span>
                      </div>
                      <span className="text-sm">Auto-generated</span>
                    </div>

                    <div className="border rounded p-3 flex items-center justify-between bg-gray-50 text-gray-500">
                      <div className="flex items-center">
                        <Tag size={18} className="mr-2" />
                        <span>Last Modified</span>
                      </div>
                      <span className="text-sm">-</span>
                    </div>
                  </div>
                </Card>

                <div className="flex justify-between">
                  <Button
                    variant="text"
                    className="border border-gray-300"
                    onClick={() => handleNavigation("/admin/researchs")}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="primary"
                    type="submit"
                    disabled={loading}
                    className="flex items-center disabled:opacity-50"
                  >
                    {loading ? (
                      <>
                        <span className="mr-2">Saving...</span>
                      </>
                    ) : (
                      <>
                        <Save size={16} className="mr-2" />
                        Save Research
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </div>
          </form>
        </div>
      </div>

      {/* Markdown Help Modal */}
      <Modal
        isOpen={showHelpModal}
        onClose={() => setShowHelpModal(false)}
        title="Markdown Guide"
        actions={
          <Button variant="primary" onClick={() => setShowHelpModal(false)}>
            Got it
          </Button>
        }
      >
        <div className="space-y-4">
          <p>You can use Markdown to format your research content:</p>

          <div className="grid grid-cols-2 gap-4">
            <div className="border rounded p-2">
              <p className="font-bold mb-1">Heading 1</p>
              <code className="bg-gray-100 p-1 text-sm"># Your Heading</code>
            </div>
            <div className="border rounded p-2">
              <p className="font-bold mb-1">Heading 2</p>
              <code className="bg-gray-100 p-1 text-sm">
                ## Your Subheading
              </code>
            </div>
            <div className="border rounded p-2">
              <p className="font-bold mb-1">Bold Text</p>
              <code className="bg-gray-100 p-1 text-sm">**bold text**</code>
            </div>
            <div className="border rounded p-2">
              <p className="font-bold mb-1">Bullet List</p>
              <code className="bg-gray-100 p-1 text-sm">
                - Item 1<br />- Item 2
              </code>
            </div>
            <div className="border rounded p-2">
              <p className="font-bold mb-1">Numbered List</p>
              <code className="bg-gray-100 p-1 text-sm">
                1. First item
                <br />
                2. Second item
              </code>
            </div>
          </div>
        </div>
      </Modal>

      {/* Unsaved Changes Modal */}
      <Modal
        isOpen={showUnsavedModal}
        onClose={() => setShowUnsavedModal(false)}
        title="Unsaved Changes"
        actions={
          <>
            <Button variant="text" onClick={() => setShowUnsavedModal(false)}>
              Cancel
            </Button>
            <Button variant="danger" onClick={confirmNavigation}>
              Discard Changes
            </Button>
          </>
        }
      >
        <p>
          You have unsaved changes that will be lost. Are you sure you want to
          continue?
        </p>
      </Modal>
    </AdminLayout>
  );
};

export default AddResearch;
